/**
 * `DecisionTreeApi` against the real SalesRecommendationHub.
 *
 * ## The client is injected, and that is on purpose
 *
 * `RealSfdcAccountApi` in this module built its SDK client at module scope, and the result was 0%
 * coverage: testing it would have meant mocking the whole AWS SDK, which measures the fidelity of
 * the mock rather than the correctness of the code. Taking the client as a constructor argument —
 * defaulting to the shared one — makes every operation below testable with a four-line stub, and
 * the only untested line is the default argument. Same behaviour, 0% versus ~100%.
 *
 * ## What this layer is responsible for
 *
 * Mapping, and nothing else. `getCpaClient()` already owns the auth (Federate id_token → Cognito →
 * SigV4 for us-east-1, plus the same token as `X-Id-Token`), and `DecisionTreeStore` on the service
 * owns the rules. This file turns our shapes into commands and the service's errors into ours.
 *
 * ## Timestamps cross as strings here and as Dates on the wire
 *
 * Our interface uses ISO-8601 strings because that is what a conditional write compares and what
 * the document carries; the generated client uses `Date`. The conversion happens here, in both
 * directions, so the rest of the app never holds a `Date` it has to remember to format. A `Date`
 * loses nothing on the way out (ISO parses exactly) and nothing on the way back for our purposes:
 * the service truncates to milliseconds precisely so a round trip can be compared for equality.
 */
import {
  CreateDecisionTreeDraftCommand,
  CreateDecisionTreeVersionCommand,
  DeleteDecisionTreeDraftCommand,
  GetDecisionTreeDraftCommand,
  GetDecisionTreeVersionCommand,
  ListDecisionTreeEditorsCommand,
  ListDecisionTreeVersionsCommand,
  PutDecisionTreeDraftCommand,
  UpdateDecisionTreeActiveVersionCommand,
  getCpaClient,
} from './CpaClient';
import {
  ConflictError,
  DecisionTreeConflictError,
  DecisionTreeValidationError,
  NotFoundError,
} from './treeApiTypes';
import type {
  DecisionTreeApi,
  DecisionTreeDraftData,
  DecisionTreeEditingContext,
  DecisionTreeVersionData,
  DecisionTreeVersionSummary,
  DraftDisposition,
  TreeProblem,
} from './treeApiTypes';

/** Just enough of the generated client to send a command — the seam the tests stub. */
export interface CpaSender {
  send(command: unknown): Promise<unknown>;
}

/**
 * The client, resolved on FIRST SEND rather than at construction.
 *
 * `getCpaClient()` throws synchronously when the stage has no endpoint configured
 * (`cpaApiEndpoint === 'TBD'`). As a default constructor argument that throw happened while
 * `getDecisionTreeApi` was still building the api object — so on such a stage, turning on
 * `?tree=shadow` took down the whole editor load instead of just disabling the shadow writes.
 * That is precisely the guarantee this feature claims to keep, broken by the way the dependency
 * was obtained rather than by anything it does.
 *
 * Deferring it means a misconfigured stage fails ONE shadow request, inside the promise the
 * dual-write wrapper already swallows and logs.
 */
type SenderSource = CpaSender | (() => CpaSender);

function resolve(source: SenderSource): CpaSender {
  return typeof source === 'function' ? source() : source;
}

const iso = (d: unknown): string =>
  d instanceof Date ? d.toISOString() : typeof d === 'string' ? d : '';

const isoOpt = (d: unknown): string | undefined =>
  d === undefined || d === null ? undefined : iso(d);

export class RealDecisionTreeApi implements DecisionTreeApi {
  /**
   * Takes a sender, or a thunk for one. The default is a THUNK on purpose — see `SenderSource`.
   * Tests pass the object directly, which is the whole reason this class is testable at all.
   */
  constructor(
    private readonly source: SenderSource = () => getCpaClient() as unknown as CpaSender,
  ) {}

  async createDraft(input: { treeId: string; baseVersion?: number; clientToken?: string }) {
    const out = await this.send(
      new CreateDecisionTreeDraftCommand({
        treeId: input.treeId,
        baseVersion: input.baseVersion,
        clientToken: input.clientToken,
      }),
    );
    return { draft: draftOf(out.draft), context: contextOf(out.context) };
  }

  async getDraft(input: { treeId: string }) {
    const out = await this.send(new GetDecisionTreeDraftCommand({ treeId: input.treeId }));
    return { draft: draftOf(out.draft), context: contextOf(out.context) };
  }

  async putDraft(input: {
    treeId: string;
    document: string;
    schemaVersion: number;
    expectedUpdatedAt: string;
  }) {
    const out = await this.send(
      new PutDecisionTreeDraftCommand({
        treeId: input.treeId,
        document: input.document,
        schemaVersion: input.schemaVersion,
        expectedUpdatedAt: new Date(input.expectedUpdatedAt),
      }),
    );
    return { draft: draftOf(out.draft), context: contextOf(out.context) };
  }

  async deleteDraft(input: { treeId: string; expectedUpdatedAt: string }): Promise<void> {
    await this.send(
      new DeleteDecisionTreeDraftCommand({
        treeId: input.treeId,
        expectedUpdatedAt: new Date(input.expectedUpdatedAt),
      }),
    );
  }

  async listEditors(input: { treeId: string }) {
    const out = await this.send(new ListDecisionTreeEditorsCommand({ treeId: input.treeId }));
    return { context: contextOf(out.context) };
  }

  async commitVersion(input: {
    treeId: string;
    expectedDraftUpdatedAt: string;
    documentSha256: string;
    baseVersion: number;
    note: string;
    mergedFrom?: number[];
    clientToken?: string;
  }) {
    const out = await this.send(
      new CreateDecisionTreeVersionCommand({
        treeId: input.treeId,
        expectedDraftUpdatedAt: new Date(input.expectedDraftUpdatedAt),
        documentSha256: input.documentSha256,
        baseVersion: input.baseVersion,
        mergedFrom: input.mergedFrom,
        note: input.note,
        clientToken: input.clientToken,
      }),
    );
    return {
      version: versionOf(out.version),
      // Unknown values are read as DELETE_FAILED, not DELETED: the enum is open to additions, and
      // guessing "it was removed" would leave a real leftover draft invisible.
      draft: (out.draft === 'DELETED' ? 'DELETED' : 'DELETE_FAILED') as DraftDisposition,
    };
  }

  async getVersion(input: { treeId: string; version: number }) {
    const out = await this.send(
      new GetDecisionTreeVersionCommand({ treeId: input.treeId, version: input.version }),
    );
    return { version: versionOf(out.version) };
  }

  async listVersions(input: { treeId: string; maxResults?: number; nextToken?: string }) {
    const out = await this.send(
      new ListDecisionTreeVersionsCommand({
        treeId: input.treeId,
        maxResults: input.maxResults,
        nextToken: input.nextToken,
      }),
    );
    return {
      versions: (out.versions ?? []).map(summaryOf),
      nextToken: out.nextToken ?? undefined,
    };
  }

  async publishVersion(input: { treeId: string; version: number; expectedUpdatedAt: string }) {
    const out = await this.send(
      new UpdateDecisionTreeActiveVersionCommand({
        treeId: input.treeId,
        version: input.version,
        expectedUpdatedAt: new Date(input.expectedUpdatedAt),
      }),
    );
    return { version: versionOf(out.version) };
  }

  /**
   * Send, and turn the service's errors into ours.
   *
   * The translation matters because the UI switches on these: a 409 carrying `headVersion` is what
   * lets the save dialog offer a merge instead of just saying no, and a 400 carrying `problems` is
   * what lets it list the defects rather than one at a time. Collapsing them into `Error` would
   * leave the UI with nothing but a string.
   */
  private async send(command: unknown): Promise<AnyOut> {
    try {
      // Inside the try: resolving the client can itself throw (an unconfigured stage), and that
      // failure belongs to the request, not to whoever constructed this object.
      return ((await resolve(this.source).send(command)) ?? {}) as AnyOut;
    } catch (e) {
      throw translate(e);
    }
  }
}

/* ── Error translation ──────────────────────────────────────────────────────── */

/**
 * What `send` resolves to, as far as this file cares.
 *
 * Previously written as `Record<string, never> & { draft?: unknown; ... }`, which looked
 * permissive and was the opposite: `Record<string, never>` is the index signature
 * `{ [k: string]: never }`, so intersecting it collapsed EVERY member to `unknown & never` =
 * `never`. `never` is assignable to everything, so every mapper call below type-checked no
 * matter what it was handed — the response mapping had no type checking at all, which is
 * exactly the part most likely to drift when the model changes.
 */
type AnyOut = {
  draft?: unknown;
  context?: unknown;
  version?: unknown;
  versions?: unknown[];
  nextToken?: string | null;
};

interface ServiceError {
  name?: string;
  message?: string;
  headVersion?: number;
  headAuthor?: string;
  headCreatedAt?: unknown;
  problems?: TreeProblem[];
  $metadata?: { httpStatusCode?: number };
}

export function translate(e: unknown): Error {
  const err = (e ?? {}) as ServiceError;
  const message = err.message ?? 'Decision tree request failed';

  if (err.name === 'ConflictException') {
    // The head rides along on the 409 so the client can fetch base and theirs without another
    // round trip. Only when it is actually there -- a version-less conflict is still a conflict.
    return err.headVersion !== undefined
      ? new DecisionTreeConflictError(message, err.headVersion, err.headAuthor, isoOpt(err.headCreatedAt))
      : new ConflictError(message);
  }
  if (err.name === 'InvalidInputException' && err.problems?.length) {
    return new DecisionTreeValidationError(message, err.problems);
  }
  if (err.name === 'ResourceNotFoundException') {
    return new NotFoundError(message);
  }
  // Anything else -- including InvalidInputException with no problems list -- keeps its own
  // identity rather than being dressed up as one of ours.
  return e instanceof Error ? e : new Error(message);
}

/* ── Wire to ours ───────────────────────────────────────────────────────────── */

function draftOf(d: unknown): DecisionTreeDraftData {
  const w = (d ?? {}) as Record<string, unknown>;
  return {
    treeId: String(w.treeId ?? ''),
    owner: String(w.owner ?? ''),
    document: String(w.document ?? ''),
    schemaVersion: Number(w.schemaVersion ?? 0),
    baseVersion: Number(w.baseVersion ?? 0),
    createdAt: iso(w.createdAt),
    updatedAt: iso(w.updatedAt),
  };
}

function versionOf(v: unknown): DecisionTreeVersionData {
  const w = (v ?? {}) as Record<string, unknown>;
  return {
    treeId: String(w.treeId ?? ''),
    version: Number(w.version ?? 0),
    document: String(w.document ?? ''),
    schemaVersion: Number(w.schemaVersion ?? 0),
    status: (w.status ?? 'draft') as DecisionTreeVersionData['status'],
    baseVersion: w.baseVersion === undefined ? undefined : Number(w.baseVersion),
    mergedFrom: (w.mergedFrom as number[] | undefined) ?? undefined,
    documentSha256: w.documentSha256 === undefined ? undefined : String(w.documentSha256),
    note: w.note === undefined ? undefined : String(w.note),
    author: w.author === undefined ? undefined : String(w.author),
    createdAt: iso(w.createdAt),
    updatedAt: iso(w.updatedAt),
    publishedBy: w.publishedBy === undefined ? undefined : String(w.publishedBy),
    publishedAt: isoOpt(w.publishedAt),
  };
}

function summaryOf(v: unknown): DecisionTreeVersionSummary {
  const w = (v ?? {}) as Record<string, unknown>;
  const change = w.changeSummary as Record<string, unknown> | undefined;
  return {
    treeId: String(w.treeId ?? ''),
    version: Number(w.version ?? 0),
    status: (w.status ?? 'draft') as DecisionTreeVersionSummary['status'],
    baseVersion: w.baseVersion === undefined ? undefined : Number(w.baseVersion),
    mergedFrom: (w.mergedFrom as number[] | undefined) ?? undefined,
    note: w.note === undefined ? undefined : String(w.note),
    author: w.author === undefined ? undefined : String(w.author),
    createdAt: iso(w.createdAt),
    updatedAt: iso(w.updatedAt),
    changeSummary: change
      ? {
          added: Number(change.added ?? 0),
          modified: Number(change.modified ?? 0),
          removed: Number(change.removed ?? 0),
        }
      : undefined,
  };
}

function contextOf(c: unknown): DecisionTreeEditingContext {
  const w = (c ?? {}) as Record<string, unknown>;
  const editors = (w.otherEditors as Record<string, unknown>[] | undefined) ?? [];
  return {
    headVersion: Number(w.headVersion ?? 0),
    activeVersion: w.activeVersion === undefined ? undefined : Number(w.activeVersion),
    otherEditors: editors.map((e) => ({
      owner: String(e.owner ?? ''),
      baseVersion: Number(e.baseVersion ?? 0),
      updatedAt: iso(e.updatedAt),
    })),
  };
}
